<template>
    <AppHead title="My Spending" />
    <div class="spending-page px-4 md:px-8">
        <!-- Transaction Detail Sidebar -->
        <TransactionSidebar
            :is-open="showTransactionSidebar"
            :transaction="selectedTransaction"
            :current-index="selectedTransactionIndex"
            :total-count="flatTransactions.length"
            :categories="categories"
            :can-edit="isSubscribed"
            @close="closeTransactionSidebar"
            @navigate="handleNavigate"
            @update="handleTransactionUpdate"
            @delete="handleTransactionDelete"
            @hide="handleTransactionHide"
            @upgrade="showUpgradeModal = true"
        />
        <!-- Page Header -->
        <section class="w-full flex flex-col">
            <header class="flex justify-between items-center mb-5 sm:mb-6">
                <h1 class="heading-xl text-fg-primary">My spending</h1>
                <!-- Page actions (v2 spec: Connect bank / Manage connections / Turn off) -->
                <ButtonPopover
                    icon="dots-vertical"
                    iconClass="w-5 h-5 icons-primary"
                    buttonClass="btn-base btn-white !p-2 h-10"
                    placement="bottom-end"
                    popoverClass="bg-bg-snow rounded-lg shadow-lg min-w-52 z-30 outline outline-1 outline-offset-[-1px] outline-border-base p-1.5"
                >
                    <template #default="{ closePopover }">
                        <ul>
                            <li>
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        () => {
                                            showConnectBankModal = true;
                                            closePopover();
                                        }
                                    "
                                >
                                    <Icon
                                        icon="building"
                                        class="w-5 h-5 text-fg-tertiary"
                                    />
                                    <span>Connect bank</span>
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        () => {
                                            goToConnectBank();
                                            closePopover();
                                        }
                                    "
                                >
                                    <Icon
                                        icon="link"
                                        class="w-5 h-5 text-fg-tertiary"
                                    />
                                    <span>Manage connections</span>
                                </button>
                            </li>
                            <li v-if="canOptOut">
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        () => {
                                            showDisableModal = true;
                                            closePopover();
                                        }
                                    "
                                >
                                    <Icon
                                        icon="eye-off"
                                        class="w-5 h-5 text-fg-tertiary"
                                    />
                                    <span>Turn off My spending</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
            </header>
        </section>

        <!-- Opt-in landing (paid user with My Spending turned off) -->
        <div
            v-if="showOptIn"
            class="bg-bg-snow rounded-xl border border-border-base overflow-hidden"
        >
            <!-- Preview illustration -->
            <SpendingPreview />

            <!-- Copy + CTA -->
            <div class="px-6 py-8 sm:px-10">
                <h2
                    class="text-2xl sm:text-3xl font-bold text-fg-primary leading-tight"
                >
                    Turn on <span class="text-10x-orange">My spending</span>
                    <br />
                    and earn more on every purchase
                </h2>
                <ul class="mt-6 space-y-4 max-w-lg">
                    <li
                        v-for="benefit in optInBenefits"
                        :key="benefit.title"
                        class="flex items-start gap-3"
                    >
                        <span
                            class="flex-shrink-0 w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center"
                        >
                            <Icon
                                :icon="benefit.icon"
                                class="w-4 h-4 text-10x-orange"
                            />
                        </span>
                        <div>
                            <div class="text-sm font-semibold text-fg-primary">
                                {{ benefit.title }}
                            </div>
                            <div class="text-sm text-fg-tertiary">
                                {{ benefit.subtitle }}
                            </div>
                        </div>
                    </li>
                </ul>
                <div class="mt-8 flex justify-end">
                    <button
                        type="button"
                        class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                        @click="turnOnSpending"
                    >
                        Turn on My spending
                    </button>
                </div>
            </div>
        </div>

        <!-- No-connection state (new users with no connected bank) -->
        <div
            v-if="hasNoConnection"
            class="bg-bg-snow rounded-xl border border-border-base overflow-hidden px-6 py-8 sm:py-16 flex flex-col items-start text-left sm:items-center sm:text-center"
        >
            <!-- Icon header (stays centered on mobile) -->
            <div class="self-center flex items-center gap-3 mb-6 sm:mb-8">
                <div
                    class="w-12 h-12 rounded-full bg-bg-base flex items-center justify-center"
                >
                    <Icon icon="building" class="w-6 h-6 text-fg-tertiary" />
                </div>
                <div
                    class="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center"
                >
                    <span class="text-white font-bold text-sm">10x</span>
                </div>
            </div>

            <!-- Faint product preview (v2 mobile connect screen) -->
            <SpendingPreview
                class="sm:hidden self-stretch -mx-6 mb-6 rounded-none"
                padClass="px-4 py-6 min-h-[200px]"
            />

            <h2 class="text-2xl font-semibold text-fg-primary">
                Connect bank to use
                <span class="text-10x-orange">My spending</span>
            </h2>
            <p class="text-sm text-fg-tertiary mt-2 max-w-md">
                Connect a bank account to load your spending data and sync your
                bonus balance and linked cards automatically.
            </p>

            <!-- Steps -->
            <ol class="mt-6 space-y-3 text-left max-w-md w-full">
                <li
                    v-for="(step, i) in connectSteps"
                    :key="i"
                    class="flex items-start gap-3"
                >
                    <span
                        class="flex-shrink-0 w-6 h-6 rounded-full bg-bg-base text-fg-secondary text-xs font-medium flex items-center justify-center"
                    >
                        {{ i + 1 }}
                    </span>
                    <span class="text-sm text-fg-secondary">{{ step }}</span>
                </li>
            </ol>

            <!-- CTA (full-width on mobile per the v2 connect screen) -->
            <button
                type="button"
                class="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                @click="goToConnectBank"
            >
                <Icon icon="building" class="w-5 h-5" />
                Connect bank
            </button>
        </div>

        <!-- Summary cards (paid members only; free users see the banner below) -->
        <div
            v-if="isSubscribed && !hasNoConnection && !showOptIn"
            class="mb-5 sm:mb-6 bg-bg-snow rounded-xl border border-border-base flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-border-base overflow-visible"
        >
            <SummaryCard :label="summarySpentLabel" :value="summarySpent" />
            <SummaryCard
                v-if="summaryPointsEarned !== null"
                :label="summaryEarnedLabel"
                :value="summaryPointsEarned"
                unit="pts"
            >
                <template #label-suffix>
                    <Icon
                        v-tooltip="
                            'Estimated rewards earned on the spending shown, based on each card\'s earn rates.'
                        "
                        icon="info"
                        class="w-4 h-4 text-fg-tertiary"
                    />
                </template>
                <template #value-suffix>
                    <!-- Per-card breakdown popover (v2 spec: cards that earned
                         rewards, highest first) -->
                    <ButtonPopover
                        v-if="summaryCardsBreakdown.length"
                        :teleport="true"
                        buttonClass="!p-1 rounded-md hover:bg-bg-base"
                        placement="bottom-start"
                        popoverClass="bg-bg-snow rounded-lg shadow-lg w-[460px] max-w-[90vw] z-50 outline outline-1 outline-offset-[-1px] outline-border-base p-2"
                    >
                        <template #button-content>
                            <Icon
                                icon="chevron-down-small"
                                class="w-5 h-5 text-fg-tertiary"
                            />
                        </template>
                        <template #default>
                            <div class="max-h-64 overflow-y-auto pr-1">
                                <div
                                    v-for="entry in summaryCardsBreakdown"
                                    :key="entry.account_guid"
                                    class="flex items-center gap-3 p-2 rounded-md hover:bg-bg-subtle"
                                >
                                    <img
                                        v-if="entry.card_image"
                                        :src="`${cdnUrl}/${entry.card_image.path}/_small/${entry.card_image.file_name}`"
                                        :alt="entry.card_name"
                                        class="w-10 h-6 rounded-sm object-cover flex-shrink-0"
                                    />
                                    <div
                                        v-else
                                        class="w-10 h-6 rounded-sm bg-gradient-to-r from-gray-700 to-gray-500 flex-shrink-0"
                                    ></div>
                                    <div
                                        class="flex-1 min-w-0 flex items-center gap-1"
                                    >
                                        <span
                                            class="body-md text-fg-primary line-clamp-2 break-words"
                                        >
                                            {{ entry.card_name }}
                                        </span>
                                        <span
                                            v-if="entry.card_last_four"
                                            class="body-md text-fg-tertiary flex-shrink-0"
                                        >
                                            ({{ entry.card_last_four }})
                                        </span>
                                    </div>
                                    <span
                                        class="flex-shrink-0 inline-flex items-baseline gap-1"
                                    >
                                        <span class="body-md-500 text-fg-primary">
                                            {{ formatThousand(entry.points) }}
                                        </span>
                                        <span class="body-md text-fg-tertiary">
                                            pts
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </template>
                    </ButtonPopover>
                </template>
            </SummaryCard>
            <SummaryCard
                v-if="summaryUntapped !== null"
                label="Untapped rewards"
                :value="summaryUntappedDisplay"
                unit="pts"
                accent
            />
        </div>

        <!-- Upgrade banner (free users, dismissible) -->
        <div
            v-else-if="!isSubscribed && !bannerDismissed"
            class="mb-5 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-orange-200 bg-orange-50 px-5 py-4"
        >
            <div class="flex items-start gap-3">
                <Icon
                    icon="lock"
                    class="w-5 h-5 text-10x-orange flex-shrink-0 mt-0.5"
                />
                <div>
                    <p class="text-sm font-semibold text-fg-primary">
                        Unlock My spending feature
                    </p>
                    <p class="text-sm text-fg-secondary">
                        Upgrade to 10xTravel+ to maximize the rewards on your
                        spending.
                    </p>
                </div>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-fg-secondary rounded-lg hover:bg-orange-100 transition-colors"
                    @click="bannerDismissed = true"
                >
                    Dismiss
                </button>
                <button
                    type="button"
                    class="whitespace-nowrap px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                    @click="showUpgradeModal = true"
                >
                    Upgrade
                </button>
            </div>
        </div>

        <!-- Mobile period control (full-width; desktop has it inline in the bar) -->
        <div
            v-if="!hasNoConnection && !showOptIn"
            class="sm:hidden mb-3"
        >
            <PeriodControl v-model="period" :full-width="true" />
        </div>

        <!-- Filters Bar (relative z-20 so its popovers stack above the table) -->
        <div
            v-if="!hasNoConnection && !showOptIn"
            class="relative z-20 flex items-center justify-between gap-4 sm:pb-6"
        >
            <!-- Mobile Filters Button -->
            <button
                class="btn-base btn-white sm:!hidden"
                @click="showMobileFilters = true"
            >
                <Icon icon="filters" class="w-4 h-4" />
                Filters
                <span
                    v-if="hasActiveFilters"
                    class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                >
                    {{ activeFilterCount }}
                </span>
            </button>
            <!-- Desktop Filters -->
            <div class="sm:flex items-center gap-3 hidden flex-wrap">
                <!-- Period / date range -->
                <PeriodControl v-model="period" />

                <!-- User Filter (only show if multiple users) -->
                <ButtonPopover
                    v-if="availableUsers.length > 1"
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            User
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedUsers.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedUsers.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li v-for="user in availableUsers" :key="user.id">
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="toggleUserSelection(user)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="isUserSelected(user)"
                                        @click.stop
                                        @change="toggleUserSelection(user)"
                                    />
                                    <div
                                        class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-bg-base"
                                    >
                                        <Icon
                                            :icon="
                                                user.is_business
                                                    ? 'business'
                                                    : 'user'
                                            "
                                            class="w-3 h-3 text-fg-tertiary"
                                        />
                                    </div>
                                    <div class="flex flex-col items-start">
                                        <span>{{ user.name }}</span>
                                    </div>
                                </button>
                            </li>
                            <li v-if="selectedUsers.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="resetUserFilter()"
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>

                <!-- Card Filter -->
                <ButtonPopover
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                    popoverClass="bg-bg-snow rounded-lg shadow-lg w-[412px] z-30 outline outline-1 outline-offset-[-1px] outline-border-base p-2"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Card
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedCards.length > 0"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedCards.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <div class="divide-y divide-slate-200">
                            <div class="pb-2">
                                <UiInput
                                    id="card-search"
                                    label="Search cards"
                                    placeholder="Search cards..."
                                    v-model:inputValue="cardQuery"
                                    :options="{
                                        hideLabel: true,
                                        inputClass:
                                            'text-sm border-none py-0.5',
                                        icon: 'search',
                                    }"
                                />
                            </div>
                            <div class="max-h-48 overflow-y-auto">
                                <div
                                    v-if="filteredUsersWithCards.length === 0"
                                    class="p-2 text-fg-tertiary text-sm gap-2"
                                >
                                    No cards found
                                </div>
                                <div
                                    v-for="user in filteredUsersWithCards"
                                    :key="user.id"
                                    class="mt-2"
                                >
                                    <div
                                        class="p-2 text-sm font-medium text-fg-tertiary flex items-center gap-2"
                                    >
                                        <Icon icon="user" class="w-5 h-5" />
                                        {{ user.name }}
                                    </div>
                                    <button
                                        v-for="card in user.cards"
                                        :key="`${user.id}-${card.account_guid}`"
                                        type="button"
                                        class="w-full flex items-center gap-2 p-2 text-fg-primary text-sm rounded hover:bg-bg-base focus:bg-bg-base focus:ring-2 focus:ring-inset focus:ring-slate-300 active:bg-slate-200 transition-colors cursor-pointer outline-none font-normal"
                                        :class="{
                                            'font-medium bg-bg-subtle':
                                                isCardSelected(card),
                                        }"
                                        :title="`${card.name} (${card.last_four})`"
                                        @click="toggleCardSelection(card)"
                                    >
                                        <div class="flex-shrink-0">
                                            <input
                                                type="checkbox"
                                                :checked="isCardSelected(card)"
                                                class="form-checkbox rounded text-10x-orange"
                                                @click.stop="
                                                    toggleCardSelection(card)
                                                "
                                            />
                                        </div>
                                        <div class="flex-shrink-0">
                                            <ImageLoader
                                                class="block rounded-sm"
                                            >
                                                <template #image>
                                                    <img
                                                        :src="`${cdnUrl}/${card.card_image?.path}/_small/${card.card_image?.file_name}`"
                                                        class="h-5 w-[31.72px] rounded-sm"
                                                        loading="eager"
                                                    />
                                                </template>
                                                <template #preloader>
                                                    <div
                                                        class="h-5 w-[31.72px] rounded-sm bg-bg-base"
                                                    ></div>
                                                </template>
                                                <template #error>
                                                    <Icon
                                                        icon="credit-card"
                                                        class="w-8 h-5"
                                                    />
                                                </template>
                                            </ImageLoader>
                                        </div>
                                        <div
                                            class="flex-1 min-w-0 flex items-center gap-1"
                                        >
                                            <span class="truncate">
                                                {{ card.name }}
                                            </span>
                                            <span
                                                class="flex shrink-0"
                                                v-if="card.last_four"
                                            >
                                                ({{ card.last_four }})
                                            </span>
                                        </div>
                                    </button>
                                </div>
                                <div v-if="selectedCards.length > 0">
                                    <button
                                        type="button"
                                        class="w-full flex items-center gap-2 p-2 text-fg-primary text-sm rounded hover:bg-bg-base focus:bg-bg-base focus:ring-2 focus:ring-inset focus:ring-slate-300 active:bg-slate-200 transition-colors cursor-pointer outline-none font-medium"
                                        @click="resetCardFilter(closePopover)"
                                    >
                                        <span>Reset</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </ButtonPopover>

                <!-- Category Filter -->
                <ButtonPopover
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Category
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedCategories.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedCategories.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li v-for="category in categories" :key="category">
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="toggleCategorySelection(category)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="isCategorySelected(category)"
                                        @click.stop
                                        @change="
                                            toggleCategorySelection(category)
                                        "
                                    />
                                    <Icon
                                        :icon="iconForCategory(category)"
                                        class="w-4 h-4 text-fg-tertiary flex-shrink-0"
                                    />
                                    <span>{{ category }}</span>
                                </button>
                            </li>
                            <li v-if="selectedCategories.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="resetCategoryFilter()"
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>

                <!-- Rewards Filter -->
                <ButtonPopover
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Rewards
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedRewardFilters.length > 0"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedRewardFilters.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default>
                        <ul>
                            <li
                                v-for="option in rewardFilterOptions"
                                :key="option.value"
                            >
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="toggleRewardFilter(option.value)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="
                                            isRewardFilterSelected(option.value)
                                        "
                                        @click.stop
                                        @change="toggleRewardFilter(option.value)"
                                    />
                                    <span>{{ option.label }}</span>
                                </button>
                            </li>
                            <li v-if="selectedRewardFilters.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 body-md text-fg-primary rounded-md text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors flex items-center gap-2"
                                    @click="resetRewardFilter()"
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>

                <!-- Clear filters button - only shown when filters are active -->
                <button
                    v-if="hasActiveFilters"
                    class="btn-base btn-transparent !pl-4 !pr-3 !py-1.5"
                    @click="resetFilters"
                >
                    <span class="text-primary text-sm font-medium">
                        Reset all
                    </span>
                    <Icon icon="x" class="w-3 h-3 icons-primary" />
                </button>
            </div>

            <!-- Mobile sort + display (open bottom-sheets) -->
            <div class="flex items-center gap-2 sm:hidden">
                <button
                    type="button"
                    class="btn-base btn-white !p-2 h-10"
                    aria-label="Sort"
                    @click="showMobileSort = true"
                >
                    <Icon icon="sort" class="w-5 h-5 icons-primary" />
                </button>
                <button
                    type="button"
                    class="btn-base btn-white !p-2 h-10"
                    aria-label="Display settings"
                    @click="showMobileDisplay = true"
                >
                    <Icon icon="gear" class="w-5 h-5 icons-primary" />
                </button>
            </div>

            <!-- Display settings (desktop popover) -->
            <ButtonPopover
                class="hidden sm:block"
                buttonClass="btn-base btn-white !pl-3 !pr-3 h-10"
                placement="bottom-end"
                :closeOnSelect="false"
                popoverClass="bg-bg-snow rounded-lg shadow-lg w-64 z-30 outline outline-1 outline-offset-[-1px] outline-border-base p-3"
            >
                <template #button-content>
                    <Icon icon="gear" class="w-5 h-5 icons-primary" />
                    <span class="text-primary text-sm font-medium hidden md:inline">
                        Display
                    </span>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default>
                    <section class="flex flex-col">
                        <!-- Sort by -->
                        <label
                            class="w-full h-9 p-2 rounded flex justify-between items-center"
                        >
                            <div class="flex items-center gap-2 text-fg-primary">
                                <Icon icon="sort" class="w-4 h-4" />
                                <span class="text-sm font-normal">Sort by</span>
                            </div>
                            <ButtonPopover
                                buttonClass="!p-0 !justify-end btn-base btn-transparent !font-normal"
                                placement="bottom-end"
                                popoverClass="bg-bg-snow rounded-lg shadow-lg min-w-44 z-40 outline outline-1 outline-offset-[-1px] outline-border-base p-2"
                            >
                                <template #button-content>
                                    <span class="text-fg-primary text-sm">
                                        {{ getSortLabel(sortOrder) }}
                                    </span>
                                    <Icon
                                        icon="chevron-down-small"
                                        class="w-4 h-4 text-fg-tertiary"
                                    />
                                </template>
                                <template #default="{ closePopover: closeSort }">
                                    <div class="flex flex-col gap-1">
                                        <button
                                            v-for="option in sortOptions"
                                            :key="option.value"
                                            type="button"
                                            class="self-stretch p-2 rounded text-fg-secondary text-sm text-left hover:bg-bg-base focus:bg-bg-base focus:outline-none"
                                            :class="{
                                                'bg-bg-base':
                                                    sortOrder === option.value,
                                            }"
                                            @click="
                                                sortOrder = option.value;
                                                closeSort();
                                            "
                                        >
                                            {{ option.label }}
                                        </button>
                                    </div>
                                </template>
                            </ButtonPopover>
                        </label>
                        <!-- Group by -->
                        <label
                            class="w-full h-9 p-2 rounded flex justify-between items-center"
                        >
                            <div class="flex items-center gap-2 text-fg-primary">
                                <Icon icon="groups" class="w-4 h-4" />
                                <span class="text-sm font-normal">Group by</span>
                            </div>
                            <ButtonPopover
                                buttonClass="!p-0 !justify-end btn-base btn-transparent !font-normal"
                                placement="bottom-end"
                                popoverClass="bg-bg-snow rounded-lg shadow-lg min-w-40 z-40 outline outline-1 outline-offset-[-1px] outline-border-base p-2"
                            >
                                <template #button-content>
                                    <span class="text-fg-primary text-sm">
                                        {{ getGroupByLabel(groupBy) }}
                                    </span>
                                    <Icon
                                        icon="chevron-down-small"
                                        class="w-4 h-4 text-fg-tertiary"
                                    />
                                </template>
                                <template #default="{ closePopover: closeGroup }">
                                    <div class="flex flex-col gap-1">
                                        <button
                                            v-for="option in groupByOptions"
                                            :key="option.value"
                                            type="button"
                                            class="self-stretch p-2 rounded text-fg-secondary text-sm text-left hover:bg-bg-base focus:bg-bg-base focus:outline-none"
                                            :class="{
                                                'bg-bg-base':
                                                    groupBy === option.value,
                                            }"
                                            @click="
                                                groupBy = option.value;
                                                closeGroup();
                                            "
                                        >
                                            {{ option.label }}
                                        </button>
                                    </div>
                                </template>
                            </ButtonPopover>
                        </label>
                        <hr class="my-2 border-slate-200" />
                        <!-- Show card numbers -->
                        <label
                            class="w-full h-9 p-2 rounded flex justify-between items-center"
                        >
                            <span class="text-fg-primary text-sm font-medium">
                                Show card numbers
                            </span>
                            <Switch
                                :value="showCardNumbers"
                                @input="showCardNumbers = !showCardNumbers"
                            />
                        </label>
                        <!-- Show hidden transactions -->
                        <label
                            class="w-full h-9 p-2 rounded flex justify-between items-center"
                        >
                            <span class="text-fg-primary text-sm font-medium">
                                Show hidden transactions
                            </span>
                            <Switch
                                :value="showHiddenTransactions"
                                @input="
                                    showHiddenTransactions =
                                        !showHiddenTransactions
                                "
                            />
                        </label>
                        <!-- Compact rows -->
                        <label
                            class="w-full h-9 p-2 rounded flex justify-between items-center"
                        >
                            <span class="text-fg-primary text-sm font-medium">
                                Compact rows
                            </span>
                            <Switch
                                :value="compactRows"
                                @input="compactRows = !compactRows"
                            />
                        </label>
                    </section>
                </template>
            </ButtonPopover>
        </div>

        <!-- Transactions List (blurred behind a lock overlay for free users) -->
        <div
            v-if="!hasNoConnection && !showOptIn"
            class="relative"
            :class="{ 'max-h-[540px] overflow-hidden': !isSubscribed }"
        >
            <div
                class="bg-bg-snow rounded-xl border border-border-base overflow-hidden"
                :class="{
                    'blur-[3px] pointer-events-none select-none': !isSubscribed,
                }"
            >
            <!-- List Header -->
            <div
                class="hidden sm:grid sm:grid-cols-12 gap-4 px-4 py-3 bg-bg-snow border-b border-border-base body-md text-fg-secondary"
            >
                <div :class="showUserColumn ? 'col-span-4' : 'col-span-5'">
                    Merchant
                </div>
                <div v-if="showUserColumn" class="col-span-2">User</div>
                <div :class="showUserColumn ? 'col-span-2' : 'col-span-3'">
                    Card
                </div>
                <div
                    class="col-span-2 flex items-center justify-end gap-1"
                >
                    Rewards
                    <Icon
                        v-tooltip="
                            'Estimated rewards based on each card\'s earn rates.'
                        "
                        icon="info"
                        class="w-3.5 h-3.5 text-fg-tertiary"
                    />
                </div>
                <div class="col-span-2 text-right">Amount</div>
            </div>

            <!-- Grouped Transactions -->
            <template v-for="(group, key) in groupedTransactions" :key="key">
                <!-- Group Header (hidden when grouping is off).
                     Mobile: plain label inside the card; sm+: full-width band. -->
                <div
                    v-if="groupBy !== 'none'"
                    class="px-4 pt-4 pb-1 sm:pt-2 sm:pb-2 sm:bg-bg-subtle sm:border-b sm:border-border-base"
                >
                    <span class="body-md-500 text-fg-secondary">
                        {{ getGroupLabel(key) }}
                    </span>
                </div>

                <!-- Transaction Rows -->
                <TransactionRow
                    v-for="(transaction, tIndex) in group"
                    :key="transaction.id"
                    :transaction="transaction"
                    :is-last-in-group="tIndex === group.length - 1"
                    :compact="compactRows"
                    :show-card-number="showCardNumbers"
                    :show-user="showUserColumn"
                    :user-name="getTransactionUser(transaction).name"
                    :user-is-business="
                        getTransactionUser(transaction).is_business
                    "
                    :can-edit="isSubscribed"
                    @click="openTransactionDetail(transaction)"
                    @view="openTransactionDetail"
                    @hide="handleRowHide"
                    @delete="handleRowDelete"
                    @upgrade="showUpgradeModal = true"
                />
            </template>

            <!-- Loading State -->
            <div v-if="isInitialLoad" class="relative px-4 py-12 min-h-[200px]">
                <LoadingState :loading="true" size="medium" />
            </div>

            <!-- Empty State -->
            <div
                v-else-if="Object.keys(groupedTransactions).length === 0"
                class="px-4 py-12 text-center"
            >
                <div
                    class="w-16 h-16 mx-auto mb-4 bg-bg-base rounded-full flex items-center justify-center"
                >
                    <Icon
                        icon="credit-card"
                        class="w-8 h-8 text-fg-tertiary"
                    />
                </div>
                <h3 class="heading-lg text-fg-primary mb-1">
                    No spending yet
                </h3>
                <p class="body-md text-fg-tertiary">
                    We'll display spending from your connected bank cards when
                    you have some.
                </p>
            </div>
            </div>

            <!-- Free-user lock overlay -->
            <div
                v-if="!isSubscribed"
                class="absolute inset-0 flex items-center justify-center"
            >
                <div class="flex flex-col items-center text-center px-6">
                    <div
                        class="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mb-3"
                    >
                        <Icon icon="lock" class="w-6 h-6 text-10x-orange" />
                    </div>
                    <p class="text-lg font-semibold text-fg-primary">
                        Unlock My spending
                    </p>
                    <p class="text-sm text-fg-tertiary mt-1">
                        Available with 10xTravel+
                    </p>
                    <button
                        type="button"
                        class="mt-4 px-5 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                        @click="showUpgradeModal = true"
                    >
                        Upgrade
                    </button>
                </div>
            </div>
        </div>

        <!-- Pagination (shown for all; free users see it as a teaser) -->
        <Pagination
            v-if="!isInitialLoad && totalTransactions > 0 && !hasNoConnection && !showOptIn"
            class="mt-2"
            :totalResults="totalTransactions"
            :totalPages="lastPage"
            :perPage="50"
            :currentPage="currentPage"
            :maxVisibleButtons="5"
            :showEdges="true"
            variant="boxed"
            noun="transactions"
            separator="-"
            @pageChanged="goToPage"
        />

        <!-- Upgrade modal (free users) -->
        <PaywallModal
            :is-open="showUpgradeModal"
            title="Unlock your full spending"
            description="Edit transactions, track rewards earned, and see your full spending history with 10xTravel+."
            @close="showUpgradeModal = false"
        />

        <!-- Confirm Turn Off modal (opt-out) -->
        <div
            v-if="showDisableModal"
            class="fixed inset-0 z-[1000] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
        >
            <div
                class="absolute inset-0 bg-black/30"
                @click="showDisableModal = false"
            ></div>
            <div
                class="relative bg-bg-snow rounded-xl shadow-xl w-full max-w-md p-6"
            >
                <button
                    type="button"
                    class="absolute top-4 right-4 text-fg-tertiary hover:text-fg-secondary"
                    @click="showDisableModal = false"
                >
                    <Icon icon="close" class="w-5 h-5" />
                </button>
                <div class="flex items-start gap-3">
                    <div
                        class="flex-shrink-0 w-10 h-10 rounded-full bg-danger-surface flex items-center justify-center"
                    >
                        <Icon icon="warning" class="w-5 h-5 text-danger" />
                    </div>
                    <div>
                        <h3 class="text-lg font-semibold text-fg-primary">
                            Disable My spending?
                        </h3>
                        <p class="mt-1 text-sm text-fg-tertiary">
                            Are you sure want to disable ‘My spending’ feature?
                            You can turn it on at any moment later.
                        </p>
                    </div>
                </div>
                <div class="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        class="px-4 py-2 text-sm font-medium text-fg-primary bg-bg-snow border border-border-base rounded-lg hover:bg-bg-subtle transition-colors"
                        @click="showDisableModal = false"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="px-4 py-2 text-sm font-semibold text-white bg-danger rounded-lg hover:opacity-90 transition-opacity"
                        @click="confirmDisableSpending"
                    >
                        Disable My spending
                    </button>
                </div>
            </div>
        </div>

        <!-- Add Bank Connection modal (v2 spec): opened from the actions-menu
             Connect bank item; its CTA proceeds to the wallet MX flow. -->
        <AddBankConnectionModal
            :open="showConnectBankModal"
            :steps="connectSteps"
            @close="showConnectBankModal = false"
            @connect="goToConnectBank"
        />

        <!-- Mobile Filters bottom-sheet (desktop uses the inline popovers) -->
        <UiModalMobile
            :is-open="showMobileFilters"
            title="Filters"
            @close="showMobileFilters = false"
        >
            <template #content>
                <div class="flex flex-col gap-6 pb-2">
                    <!-- User (multi-user only) -->
                    <section v-if="availableUsers.length > 1">
                        <h3 class="text-sm font-semibold text-fg-primary mb-2">
                            User
                        </h3>
                        <div class="flex flex-col">
                            <button
                                v-for="user in availableUsers"
                                :key="user.id"
                                type="button"
                                class="w-full flex items-center gap-2 p-2 body-md text-fg-primary rounded-md hover:bg-bg-subtle text-left"
                                @click="toggleUserSelection(user)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isUserSelected(user)"
                                    @click.stop
                                    @change="toggleUserSelection(user)"
                                />
                                <div
                                    class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-bg-base"
                                >
                                    <Icon
                                        :icon="user.is_business ? 'business' : 'user'"
                                        class="w-3 h-3 text-fg-tertiary"
                                    />
                                </div>
                                <span>{{ user.name }}</span>
                            </button>
                        </div>
                    </section>

                    <!-- Card -->
                    <section>
                        <h3 class="text-sm font-semibold text-fg-primary mb-2">
                            Card
                        </h3>
                        <UiInput
                            id="mobile-card-search"
                            label="Search cards"
                            placeholder="Search cards..."
                            v-model:inputValue="cardQuery"
                            :options="{
                                hideLabel: true,
                                inputClass: 'text-sm',
                                icon: 'search',
                            }"
                        />
                        <div class="mt-2 max-h-56 overflow-y-auto">
                            <div
                                v-if="filteredUsersWithCards.length === 0"
                                class="p-2 text-fg-tertiary text-sm"
                            >
                                No cards found
                            </div>
                            <div
                                v-for="user in filteredUsersWithCards"
                                :key="user.id"
                                class="mt-1"
                            >
                                <div
                                    class="p-2 text-xs font-medium text-fg-tertiary flex items-center gap-2"
                                >
                                    <Icon icon="user" class="w-4 h-4" />
                                    {{ user.name }}
                                </div>
                                <button
                                    v-for="card in user.cards"
                                    :key="`${user.id}-${card.account_guid}`"
                                    type="button"
                                    class="w-full flex items-center gap-2 p-2 text-fg-primary text-sm rounded hover:bg-bg-base text-left"
                                    :class="{
                                        'font-medium bg-bg-subtle':
                                            isCardSelected(card),
                                    }"
                                    @click="toggleCardSelection(card)"
                                >
                                    <input
                                        type="checkbox"
                                        :checked="isCardSelected(card)"
                                        class="rounded text-10x-orange"
                                        @click.stop="toggleCardSelection(card)"
                                    />
                                    <div
                                        class="w-8 h-5 rounded-sm bg-gradient-to-r from-gray-700 to-gray-500 flex-shrink-0"
                                    ></div>
                                    <span class="flex-1 min-w-0 truncate">
                                        {{ card.name }}
                                        <span
                                            v-if="card.last_four"
                                            class="text-fg-tertiary"
                                        >
                                            ({{ card.last_four }})
                                        </span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </section>

                    <!-- Category -->
                    <section>
                        <h3 class="text-sm font-semibold text-fg-primary mb-2">
                            Category
                        </h3>
                        <div class="max-h-48 overflow-y-auto flex flex-col">
                            <button
                                v-for="category in categories"
                                :key="category"
                                type="button"
                                class="w-full flex items-center gap-2 p-2 body-md text-fg-primary rounded-md hover:bg-bg-subtle text-left"
                                @click="toggleCategorySelection(category)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isCategorySelected(category)"
                                    @click.stop
                                    @change="toggleCategorySelection(category)"
                                />
                                <Icon
                                    :icon="iconForCategory(category)"
                                    class="w-4 h-4 text-fg-tertiary flex-shrink-0"
                                />
                                <span>{{ category }}</span>
                            </button>
                        </div>
                    </section>

                    <!-- Rewards -->
                    <section>
                        <h3 class="text-sm font-semibold text-fg-primary mb-2">
                            Rewards
                        </h3>
                        <div class="flex flex-col">
                            <button
                                v-for="option in rewardFilterOptions"
                                :key="option.value"
                                type="button"
                                class="w-full flex items-center gap-2 p-2 body-md text-fg-primary rounded-md hover:bg-bg-subtle text-left"
                                @click="toggleRewardFilter(option.value)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isRewardFilterSelected(option.value)"
                                    @click.stop
                                    @change="toggleRewardFilter(option.value)"
                                />
                                <span>{{ option.label }}</span>
                            </button>
                        </div>
                    </section>
                </div>
            </template>
            <template #footer>
                <div class="flex items-center gap-3">
                    <button
                        v-if="hasActiveFilters"
                        type="button"
                        class="btn-base btn-white flex-1"
                        @click="resetFilters"
                    >
                        Reset all
                    </button>
                    <button
                        type="button"
                        class="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                        @click="showMobileFilters = false"
                    >
                        Show results
                    </button>
                </div>
            </template>
        </UiModalMobile>

        <!-- Mobile Sort bottom-sheet -->
        <UiModalMobile
            :is-open="showMobileSort"
            title="Sort by"
            @close="showMobileSort = false"
        >
            <template #content>
                <div class="flex flex-col pb-2">
                    <button
                        v-for="option in sortOptions"
                        :key="option.value"
                        type="button"
                        class="w-full flex items-center gap-2 p-3 body-md text-fg-primary rounded-md hover:bg-bg-subtle text-left"
                        :class="{
                            'bg-bg-subtle font-medium':
                                sortOrder === option.value,
                        }"
                        @click="
                            sortOrder = option.value;
                            showMobileSort = false;
                        "
                    >
                        <input
                            type="radio"
                            name="mobile-sort"
                            class="text-10x-orange"
                            :checked="sortOrder === option.value"
                            @click.stop
                        />
                        <span>{{ option.label }}</span>
                    </button>
                </div>
            </template>
        </UiModalMobile>

        <!-- Mobile Display Settings bottom-sheet -->
        <UiModalMobile
            :is-open="showMobileDisplay"
            title="Display settings"
            @close="showMobileDisplay = false"
        >
            <template #content>
                <div class="flex flex-col gap-4 pb-2">
                    <!-- Group by -->
                    <section>
                        <h3 class="text-sm font-semibold text-fg-primary mb-1">
                            Group by
                        </h3>
                        <div class="flex flex-col">
                            <button
                                v-for="option in groupByOptions"
                                :key="option.value"
                                type="button"
                                class="w-full flex items-center gap-2 p-2 body-md text-fg-primary rounded-md hover:bg-bg-subtle text-left"
                                @click="groupBy = option.value"
                            >
                                <input
                                    type="radio"
                                    name="mobile-groupby"
                                    class="text-10x-orange"
                                    :checked="groupBy === option.value"
                                    @click.stop
                                />
                                <span>{{ option.label }}</span>
                            </button>
                        </div>
                    </section>
                    <!-- Toggles -->
                    <label
                        class="w-full p-2 rounded flex justify-between items-center"
                    >
                        <span class="text-fg-primary text-sm font-medium">
                            Show card numbers
                        </span>
                        <Switch
                            :value="showCardNumbers"
                            @input="showCardNumbers = !showCardNumbers"
                        />
                    </label>
                    <label
                        class="w-full p-2 rounded flex justify-between items-center"
                    >
                        <span class="text-fg-primary text-sm font-medium">
                            Show hidden transactions
                        </span>
                        <Switch
                            :value="showHiddenTransactions"
                            @input="
                                showHiddenTransactions = !showHiddenTransactions
                            "
                        />
                    </label>
                    <label
                        class="w-full p-2 rounded flex justify-between items-center"
                    >
                        <span class="text-fg-primary text-sm font-medium">
                            Compact rows
                        </span>
                        <Switch
                            :value="compactRows"
                            @input="compactRows = !compactRows"
                        />
                    </label>
                </div>
            </template>
        </UiModalMobile>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import TransactionRow from '@/components/spending/TransactionRow.vue';
import TransactionSidebar from '@/components/spending/TransactionSidebar.vue';
import SpendingPreview from '@/components/spending/SpendingPreview.vue';
import AddBankConnectionModal from '@/components/spending/AddBankConnectionModal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Switch from '@/components/ui/switch/switch.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import PeriodControl from '@/components/ui/period-control/period-control.vue';
import SummaryCard from '@/components/spending/SummaryCard.vue';
import Pagination from '@/components/ui/pagination/pagination.vue';
import PaywallModal from '@/components/ui/PaywallModal.vue';
import { useSubscription } from '@/composables/useSubscription';
import filters from '@/filters';
import { Apii } from '@/services/api.js';
import { useWalletStore } from '@/store/wallet';
import { iconForCategory } from '@/utils/category-icons';

const ApiService = new Apii();
const page = usePage();
const cdnUrl = computed(() => window.cdnUrl);

// Use wallet store for card data
const walletStore = useWalletStore();
const { walletCards, walletEntities } = storeToRefs(walletStore);

// Subscription gating (free users get a teaser + upgrade prompts)
const { isSubscribed } = useSubscription();
const showUpgradeModal = ref(false);
const bannerDismissed = ref(false);

// Sidebar state
const showTransactionSidebar = ref(false);
const selectedTransaction = ref(null);
// Derived live from the visible list so hiding/filtering while the sidebar is
// open can't leave a stale "N of M" position (flatTransactions is defined
// below; computed evaluation is lazy).
const selectedTransactionIndex = computed(() => {
    if (!selectedTransaction.value) {
        return 0;
    }
    return (
        flatTransactions.value.findIndex(
            (t) => t.id === selectedTransaction.value.id
        ) + 1
    );
});

// Filters state
const period = ref({
    preset: 'all',
    start: null,
    end: null,
    label: 'All time',
});
const selectedCards = ref([]);
const selectedUsers = ref([]);
const selectedCategories = ref([]);
const selectedRewardFilters = ref([]);
const cardQuery = ref('');
const sortOrder = ref('newest');

// Sort options (surfaced in the Display menu)
const sortOptions = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'highest', label: 'Highest amount' },
    { value: 'lowest', label: 'Lowest amount' },
];

// Rewards filter options
const rewardFilterOptions = [
    { value: 'earned', label: 'Earned rewards' },
    { value: 'bonus', label: 'Bonus category' },
    { value: 'none', label: 'No rewards' },
];

// Display settings
const compactRows = ref(false);
const groupBy = ref('date');
const showCardNumbers = ref(true);
const showHiddenTransactions = ref(false);

const groupByOptions = computed(() => {
    const options = [
        { value: 'none', label: 'None' },
        { value: 'date', label: 'Date' },
        { value: 'category', label: 'Category' },
        { value: 'card', label: 'Card' },
    ];
    // Grouping by user only makes sense with more than one user.
    if (showUserColumn.value) {
        options.push({ value: 'user', label: 'User' });
    }
    return options;
});

// Mobile filters modal state
const showMobileFilters = ref(false);
// Mobile bottom-sheets for sort + display (desktop uses the inline popovers).
const showMobileSort = ref(false);
const showMobileDisplay = ref(false);

// Pagination state
const currentPage = ref(1);
const lastPage = ref(1);
const totalTransactions = ref(0);

// Loading states
const isLoading = ref(false);
const isInitialLoad = ref(true);

// Categories (these come from MX, but we'll use a static list for filtering)
const categories = ref([
    'Shopping',
    'Restaurants & Bars',
    'Groceries',
    'Travel',
    'Entertainment & Recreation',
    'Gas & Fuel',
    'Home & Utilities',
    'Health & Wellness',
    'Transfer',
    'Credit Card Payment',
    'Income',
    'Other',
]);

// Transactions data from API
const transactions = ref([]);

// Year-based spending summary (independent of the list period filter so the
// cards always read "… in {year}" to match the design).
const summary = ref(null);
const summaryYear = new Date().getFullYear();

// Local-time YYYY-MM-DD; toISOString() is UTC and can disagree with
// summaryYear near midnight (or send start > end on Jan 1 for UTC+ users).
const localDateString = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

// v2 spec: stats follow the period control. "All time" shows the current
// year ("Spent in 2026"); any selected period shows that period's sums
// ("Spent selected period").
const isAllTimePeriod = computed(
    () => !period.value?.start && !period.value?.end
);
const summarySpentLabel = computed(() =>
    isAllTimePeriod.value ? `Spent in ${summaryYear}` : 'Spent selected period'
);
const summaryEarnedLabel = computed(() =>
    isAllTimePeriod.value
        ? `Earned in ${summaryYear}`
        : 'Earned in selected period'
);

const fetchSummary = async () => {
    try {
        const params = isAllTimePeriod.value
            ? {
                  start_date: `${summaryYear}-01-01`,
                  end_date: localDateString(new Date()),
              }
            : {
                  start_date: period.value.start,
                  end_date: period.value.end,
              };
        const response = await ApiService.apiCall(
            '/transactions/summary',
            'get',
            params
        );
        summary.value = response.data?.data || null;
    } catch (error) {
        console.error('Error fetching spending summary:', error);
    }
};

// Refetch the stats when the period changes (the transaction list has its
// own watcher).
watch(period, () => fetchSummary(), { deep: true });

const summarySpent = computed(() =>
    filters.formatSmartMoney(summary.value?.total_spend || 0)
);

// Cards that earned rewards in the period, highest first (Earned popover).
const summaryCardsBreakdown = computed(
    () => summary.value?.points_by_card || []
);
const formatThousand = filters.formatThousand;
// Points-earned / untapped are not yet returned by the summary endpoint
// (backend enabler ticket). Render those cards only once the data exists.
const summaryPointsEarned = computed(() =>
    summary.value?.total_points_earned != null
        ? filters.formatThousand(summary.value.total_points_earned) || '0'
        : null
);
const summaryUntapped = computed(() =>
    summary.value?.untapped_points != null
        ? summary.value.untapped_points
        : null
);
const summaryUntappedDisplay = computed(() =>
    summaryUntapped.value != null
        ? `+${filters.formatThousand(summaryUntapped.value) || '0'}`
        : null
);

// Monotonic sequence so a response that arrives after a newer request has
// been issued is discarded instead of clobbering the newer data. Refetches
// triggered mid-flight (filter/sort/page changes) are never dropped.
let fetchSequence = 0;

// Fetch transactions from API
const fetchTransactions = async (page = 1, append = false) => {
    const requestId = ++fetchSequence;

    isLoading.value = true;

    try {
        const params = {
            page,
            per_page: 50,
        };

        // Add date filter from the period control (omit entirely for "All time")
        if (period.value?.start) {
            params.start_date = period.value.start;
        }
        if (period.value?.end) {
            params.end_date = period.value.end;
        }

        // Card, user and category filters run server-side so pagination and
        // the total describe the filtered list (Devin, PR #1878).
        const accountGuids = accountGuidFilter.value;
        if (accountGuids !== null) {
            if (accountGuids.length === 0) {
                // e.g. a selected user owns none of the selected cards.
                transactions.value = [];
                currentPage.value = 1;
                lastPage.value = 1;
                totalTransactions.value = 0;
                return;
            }
            params.account_guids = accountGuids;
        }
        if (selectedCategories.value.length > 0) {
            params.categories = selectedCategories.value;
        }

        // Add sort
        if (sortOrder.value === 'highest' || sortOrder.value === 'lowest') {
            params.sort = 'amount';
            params.sort_dir = sortOrder.value === 'highest' ? 'desc' : 'asc';
        } else {
            params.sort = 'date';
            params.sort_dir = sortOrder.value === 'oldest' ? 'asc' : 'desc';
        }

        const response = await ApiService.apiCall(
            '/transactions',
            'get',
            params
        );

        // A newer request superseded this one; drop the stale response.
        if (requestId !== fetchSequence) {
            return;
        }

        // Axios wraps the response, so response.data is the API response
        const apiResponse = response.data;

        if (apiResponse.data) {
            // Normalize transaction data
            const normalizedTransactions = apiResponse.data.map((t) => ({
                ...t,
                // Map API fields to our expected format
                merchant_category: t.merchant_category || 'Other',
                category: t.merchant_category || 'Other',
            }));

            if (append) {
                // A deep-linked transaction may already sit in the list.
                const loadedIds = new Set(transactions.value.map((t) => t.id));
                transactions.value = [
                    ...transactions.value,
                    ...normalizedTransactions.filter(
                        (t) => !loadedIds.has(t.id)
                    ),
                ];
            } else {
                transactions.value = normalizedTransactions;
            }

            // Update pagination
            if (apiResponse.meta) {
                currentPage.value = apiResponse.meta.current_page;
                lastPage.value = apiResponse.meta.last_page;
                totalTransactions.value = apiResponse.meta.total;
            }
        }
    } catch (error) {
        if (requestId === fetchSequence) {
            console.error('Error fetching transactions:', error);
        }
    } finally {
        if (requestId === fetchSequence) {
            isLoading.value = false;
            isInitialLoad.value = false;
            maybeMarkTableSeen();
        }
    }
};

// Store account GUIDs that have transactions (from warehouse)
const transactionAccountGuids = ref(new Set());
const accountsChecked = ref(false);

// Opt-in/out (MY1-2871, MY1-3029). New users get My Spending on by default;
// existing users must opt in. Anyone may opt out while it's on. State is
// persisted server-side and surfaced via shared userData. Paid users with it
// off see the opt-in landing instead of the table.
const spendingEnabled = computed(
    () => page.props.userData?.spending_enabled !== false
);
const canOptOut = computed(() => !!page.props.userData?.spending_can_opt_out);
const showOptIn = computed(
    () => isSubscribed.value && !spendingEnabled.value
);

const optInBenefits = [
    {
        icon: 'document',
        title: 'All your transactions, one place',
        subtitle: 'All connected cards in one feed, always up to date.',
    },
    {
        icon: 'coin',
        title: 'Rewards on every purchase',
        subtitle: 'Points earned per transaction, estimated by bonus category.',
    },
    {
        icon: 'graph',
        title: 'Spend smarter, earn more',
        subtitle: 'See which card earns you more — and choose better next time.',
    },
];

const turnOnSpending = () => {
    router.post('/spending/enable', {}, { preserveScroll: true });
};

// Opt-out flow: Confirm Turn Off modal -> disable.
const showDisableModal = ref(false);
const confirmDisableSpending = () => {
    router.post(
        '/spending/disable',
        {},
        {
            preserveScroll: true,
            onFinish: () => {
                showDisableModal.value = false;
            },
        }
    );
};

// Clear the 'New' nav badge once the populated table is shown (idempotent).
const tableSeenMarked = ref(false);
const maybeMarkTableSeen = () => {
    if (
        tableSeenMarked.value ||
        page.props.userData?.spending_table_seen ||
        !spendingEnabled.value ||
        showOptIn.value ||
        hasNoConnection.value ||
        totalTransactions.value <= 0
    ) {
        return;
    }
    tableSeenMarked.value = true;
    router.post(
        '/spending/seen',
        {},
        { preserveScroll: true, preserveState: true }
    );
};

// The viewer has a bank-connected card in the wallet. Warehouse activity alone
// can't tell: a card connected before its first import has no transactions
// yet, but it is connected (Devin, PR #1878).
const hasConnectedCard = computed(() => {
    const viewerId = page.props.userData?.id;
    return (
        viewerId != null &&
        (walletCards.value || []).some(
            (card) =>
                card.member_guid &&
                !card.converted_to_manual &&
                Number(card.user_id) === Number(viewerId)
        )
    );
});

// New-user onboarding: a paid, enabled user with no connected bank accounts yet.
const hasNoConnection = computed(
    () =>
        isSubscribed.value &&
        spendingEnabled.value &&
        accountsChecked.value &&
        walletChecked.value &&
        transactionAccountGuids.value.size === 0 &&
        !hasConnectedCard.value
);

const connectSteps = [
    'Add a bank connection from your wallet.',
    'Sign in to your bank account to authorize the connection.',
    'Your spending will appear here once a bank account is connected.',
];

const goToConnectBank = () => {
    router.visit('/wallet');
};

// Add Bank Connection modal (v2 spec): shown for connect CTAs triggered from
// a populated page; the no-connection landing already shows the same steps.
const showConnectBankModal = ref(false);

// Fetch account GUIDs from transaction warehouse (to know which accounts have transactions)
const fetchTransactionAccountGuids = async () => {
    try {
        const response = await ApiService.apiCall(
            '/transactions/accounts',
            'get'
        );
        const apiResponse = response.data;
        if (apiResponse.data) {
            transactionAccountGuids.value = new Set(
                apiResponse.data.map((account) => account.account_guid)
            );
        }
    } catch (error) {
        console.error('Error fetching transaction accounts:', error);
    } finally {
        accountsChecked.value = true;
    }
};

// Ensure wallet data is loaded
const walletChecked = ref(false);
const ensureWalletLoaded = async () => {
    try {
        if (!walletCards.value || walletCards.value.length === 0) {
            await walletStore.getWalletByEntities();
        }
    } finally {
        walletChecked.value = true;
    }
};

// Get available cards from wallet data (filtered to only those with transactions)
const availableCards = computed(() => {
    if (!walletCards.value) return [];

    // Filter wallet cards to only those that have transactions
    // and have a valid guid
    const cardsWithTransactions = walletCards.value.filter(
        (card) => card.guid && transactionAccountGuids.value.has(card.guid)
    );

    // Deduplicate by GUID, preferring cards that have a card_id (matched cards)
    const cardsByGuid = new Map();
    cardsWithTransactions.forEach((card) => {
        const existing = cardsByGuid.get(card.guid);
        // Prefer the card that has a card_id (is matched to our cards table)
        if (!existing || (card.card_id && !existing.card_id)) {
            cardsByGuid.set(card.guid, card);
        }
    });

    return Array.from(cardsByGuid.values()).map((card) => {
        // Prioritize the matched card title from the card object
        // card_title may contain the MX account name as fallback
        const cardName =
            card.card?.title || card.card_title || card.name || 'Unknown Card';

        // Get card image - check multiple possible locations
        // The wallet API loads card.cardImage (snake_case in JSON: card_image)
        const cardImage =
            card.card?.card_image ||
            card.card?.cardImage ||
            card.card?.image ||
            null;

        return {
            id: card.id,
            account_guid: card.guid,
            name: cardName,
            last_four: card.last_four,
            card_image: cardImage,
            user_id: card.user_id,
            user_name: card.user_name,
            is_business: card.is_business || false,
        };
    });
});

// Get available users from wallet entities
const availableUsers = computed(() => {
    if (!walletEntities.value) return [];

    return walletEntities.value.map((entity) => ({
        id: entity.is_business ? `business-${entity.id}` : entity.id,
        name: entity.name,
        is_business: entity.is_business || false,
    }));
});

// Helper to get user name from wallet entities
const getUserNameById = (userId, isBusiness) => {
    if (!walletEntities.value) return 'Unknown User';

    const entity = walletEntities.value.find((e) => {
        if (isBusiness) {
            return e.is_business && e.id === userId;
        }
        return !e.is_business && e.id === userId;
    });

    return entity?.name || 'Unknown User';
};

// Multi-user: show the per-row User column only when more than one user.
const showUserColumn = computed(() => availableUsers.value.length > 1);

// Map account_guid -> owning user so each transaction can be attributed.
const userByAccountGuid = computed(() => {
    const map = {};
    availableCards.value.forEach((card) => {
        if (card.account_guid && !map[card.account_guid]) {
            map[card.account_guid] = {
                name:
                    card.user_name ||
                    getUserNameById(card.user_id, card.is_business),
                is_business: card.is_business || false,
            };
        }
    });
    return map;
});

const getTransactionUser = (transaction) =>
    userByAccountGuid.value[transaction?.account_guid] || {
        name: '',
        is_business: false,
    };

// Get users with their cards for grouped display in card dropdown
const usersWithCards = computed(() => {
    const users = new Map();

    availableCards.value.forEach((card) => {
        const userId = card.is_business
            ? `business-${card.user_id}`
            : card.user_id;
        const userKey = userId || 'unknown';

        if (!users.has(userKey)) {
            // Get user name from wallet entities if not on card
            const userName =
                card.user_name ||
                getUserNameById(card.user_id, card.is_business);
            users.set(userKey, {
                id: userKey,
                name: userName,
                is_business: card.is_business,
                cards: [],
            });
        }
        users.get(userKey).cards.push(card);
    });

    return Array.from(users.values());
});

// Filter users and cards based on search query
const filteredUsersWithCards = computed(() => {
    if (!cardQuery.value) {
        return usersWithCards.value;
    }
    const query = cardQuery.value.toLowerCase();
    return usersWithCards.value
        .map((user) => ({
            ...user,
            cards: user.cards.filter(
                (card) =>
                    card.name.toLowerCase().includes(query) ||
                    (card.last_four && card.last_four.includes(query))
            ),
        }))
        .filter((user) => user.cards.length > 0);
});

// Check if filters are active
const hasActiveFilters = computed(() => {
    return (
        selectedCards.value.length > 0 ||
        selectedUsers.value.length > 0 ||
        selectedCategories.value.length > 0 ||
        selectedRewardFilters.value.length > 0 ||
        period.value.preset !== 'all'
    );
});

// Count of active filters for mobile badge
const activeFilterCount = computed(() => {
    let count = 0;
    if (selectedCards.value.length > 0) count += selectedCards.value.length;
    if (selectedUsers.value.length > 0) count += selectedUsers.value.length;
    if (selectedCategories.value.length > 0)
        count += selectedCategories.value.length;
    if (selectedRewardFilters.value.length > 0)
        count += selectedRewardFilters.value.length;
    if (period.value.preset !== 'all') count += 1;
    return count;
});

// Card filter methods
const isCardSelected = (card) => {
    return selectedCards.value.some(
        (c) => c.account_guid === card.account_guid
    );
};

const toggleCardSelection = (card) => {
    const idx = selectedCards.value.findIndex(
        (c) => c.account_guid === card.account_guid
    );
    if (idx >= 0) {
        selectedCards.value.splice(idx, 1);
    } else {
        selectedCards.value.push(card);
    }
    cardQuery.value = '';
};

const resetCardFilter = (closePopover) => {
    selectedCards.value = [];
    cardQuery.value = '';
    if (closePopover) closePopover();
};

// User filter methods
const isUserSelected = (user) => {
    return selectedUsers.value.some((u) => u.id === user.id);
};

const toggleUserSelection = (user) => {
    const idx = selectedUsers.value.findIndex((u) => u.id === user.id);
    if (idx >= 0) {
        selectedUsers.value.splice(idx, 1);
    } else {
        selectedUsers.value.push(user);
    }
};

const resetUserFilter = () => {
    selectedUsers.value = [];
};

// Category filter methods
const isCategorySelected = (category) => {
    return selectedCategories.value.includes(category);
};

const toggleCategorySelection = (category) => {
    const idx = selectedCategories.value.indexOf(category);
    if (idx >= 0) {
        selectedCategories.value.splice(idx, 1);
    } else {
        selectedCategories.value.push(category);
    }
};

const resetCategoryFilter = () => {
    selectedCategories.value = [];
};

// Rewards filter methods
const isRewardFilterSelected = (value) =>
    selectedRewardFilters.value.includes(value);

const toggleRewardFilter = (value) => {
    const idx = selectedRewardFilters.value.indexOf(value);
    if (idx >= 0) {
        selectedRewardFilters.value.splice(idx, 1);
    } else {
        selectedRewardFilters.value.push(value);
    }
};

const resetRewardFilter = () => {
    selectedRewardFilters.value = [];
};

// Best-effort client-side classification for the Rewards filter using the
// per-transaction fields the warehouse enrichment already provides.
const transactionMatchesRewardFilter = (t) => {
    if (selectedRewardFilters.value.length === 0) {
        return true;
    }
    const isExpense = t.is_expense || t.type === 'DEBIT';
    const hasCard = !!t.card_name && t.card_name !== 'Unknown Card';
    const earnsRewards = isExpense && hasCard;
    return selectedRewardFilters.value.some((filter) => {
        if (filter === 'earned') {
            return earnsRewards;
        }
        if (filter === 'bonus') {
            return !!t.is_bonus_category;
        }
        if (filter === 'none') {
            return !earnsRewards;
        }
        return false;
    });
};

// Accounts the card and user filters narrow to — their intersection when
// both are set. Null when neither is set (no account filter).
const accountGuidFilter = computed(() => {
    let guids = null;

    if (selectedCards.value.length > 0) {
        guids = selectedCards.value.map((c) => c.account_guid);
    }

    if (selectedUsers.value.length > 0) {
        const userCardGuids = availableCards.value
            .filter((card) => {
                const cardUserId = card.is_business
                    ? `business-${card.user_id}`
                    : card.user_id;
                return selectedUsers.value.some((u) => u.id === cardUserId);
            })
            .map((card) => card.account_guid);
        guids =
            guids === null
                ? userCardGuids
                : guids.filter((guid) => userCardGuids.includes(guid));
    }

    return guids;
});

// Watch for filter changes and refetch (server-side filters: period, cards,
// users, categories, sort)
watch(
    [period, selectedCards, selectedUsers, selectedCategories, sortOrder],
    () => {
        currentPage.value = 1;
        fetchTransactions(1, false);
    },
    { deep: true }
);

// Initial load
onMounted(async () => {
    await Promise.all([
        ensureWalletLoaded(),
        fetchTransactionAccountGuids(),
        fetchTransactions(1, false),
        fetchSummary(),
    ]);

    // MY1-3079: a benefit usage record's Data Link lands here with the
    // transaction it came from — open it once the list has loaded.
    await openTransactionFromQuery();
});

// Group transactions by date
// Note: card, user and category filters are applied server-side; the rewards
// and hidden filters still refine the loaded page here (rewards depend on
// earn rates computed after the query).
const groupedTransactions = computed(() => {
    const groups = {};

    let filtered = transactions.value;

    // Apply rewards filter client-side
    if (selectedRewardFilters.value.length > 0) {
        filtered = filtered.filter(transactionMatchesRewardFilter);
    }

    // Hide hidden transactions unless the user opts to show them
    if (!showHiddenTransactions.value) {
        filtered = filtered.filter((t) => !t.is_hidden);
    }

    // Group by the selected dimension (sorting is done server-side)
    filtered.forEach((transaction) => {
        const key = getGroupKey(transaction);
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(transaction);
    });

    return groups;
});

// Resolve the grouping key for a transaction based on the active Group by mode.
const getGroupKey = (transaction) => {
    if (groupBy.value === 'category') {
        return (
            transaction.category || transaction.merchant_category || 'Other'
        );
    }
    if (groupBy.value === 'card') {
        return transaction.card_name || 'Unknown Card';
    }
    if (groupBy.value === 'user') {
        return getTransactionUser(transaction).name || 'Unknown User';
    }
    if (groupBy.value === 'none') {
        return 'all';
    }
    return transaction.transaction_date;
};

// Human label for a group header (date formatting only applies to date groups).
const getGroupLabel = (key) => {
    return groupBy.value === 'date' ? formatDateHeader(key) : key;
};

const getGroupByLabel = (value) =>
    groupByOptions.value.find((o) => o.value === value)?.label || 'Date';

const getSortLabel = (value) =>
    sortOptions.find((o) => o.value === value)?.label || 'Newest first';

// Flat list of filtered/sorted transactions for sidebar navigation
const flatTransactions = computed(() => {
    const allTransactions = [];
    Object.values(groupedTransactions.value).forEach((group) => {
        allTransactions.push(...group);
    });
    return allTransactions;
});

// Helper functions
const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
        return 'Today';
    }
    if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
    }

    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatCurrency = (amount) => {
    const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(Math.abs(amount));

    return amount < 0 ? `-${formatted}` : `+${formatted}`;
};

const resetFilters = () => {
    period.value = {
        preset: 'all',
        start: null,
        end: null,
        label: 'All time',
    };
    selectedCards.value = [];
    selectedUsers.value = [];
    selectedCategories.value = [];
    selectedRewardFilters.value = [];
    cardQuery.value = '';
};

const goToPage = (page) => {
    if (page === currentPage.value) {
        return;
    }
    fetchTransactions(page, false);
    if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
};

// Per-row overflow menu actions (delegate to the existing handlers).
const handleRowHide = (transaction) => {
    handleTransactionHide({
        id: transaction.id,
        is_hidden: !transaction.is_hidden,
    });
};

const handleRowDelete = (transaction) => {
    handleTransactionDelete({ id: transaction.id });
};

const openTransactionDetail = (transaction) => {
    selectedTransaction.value = transaction;
    showTransactionSidebar.value = true;
};

const closeTransactionSidebar = () => {
    showTransactionSidebar.value = false;
};

/**
 * Open the transaction named by `?transaction=<guid>` (MY1-3079). Prefer the
 * row already in the list so prev/next navigation works; otherwise fetch it
 * on its own — an older purchase may sit past the first page.
 */
const openTransactionFromQuery = async () => {
    const requestedId = new URLSearchParams(window.location.search).get(
        'transaction'
    );

    if (!requestedId) {
        return;
    }

    const loaded = transactions.value.find((t) => t.id === requestedId);

    if (loaded) {
        openTransactionDetail(loaded);

        return;
    }

    try {
        const response = await ApiService.apiCall(
            `/transactions/${encodeURIComponent(requestedId)}`,
            'get'
        );
        const transaction = response.data?.data;

        if (transaction) {
            const normalized = {
                ...transaction,
                merchant_category: transaction.merchant_category || 'Other',
                category: transaction.merchant_category || 'Other',
            };

            // Put it in the list so "N of M" and prev/next work off a real
            // position. It is older than the loaded page (or it would have
            // been in it), and the list is newest-first, so it goes last.
            if (!transactions.value.some((t) => t.id === normalized.id)) {
                transactions.value = [...transactions.value, normalized];
            }

            openTransactionDetail(normalized);
        }
    } catch (error) {
        console.error('Error opening transaction from URL:', error);
    }
};

const handleNavigate = (direction) => {
    const currentIdx = selectedTransactionIndex.value - 1;
    let newIdx;

    if (direction === 'prev' && currentIdx > 0) {
        newIdx = currentIdx - 1;
    } else if (
        direction === 'next' &&
        currentIdx < flatTransactions.value.length - 1
    ) {
        newIdx = currentIdx + 1;
    } else {
        return;
    }

    selectedTransaction.value = flatTransactions.value[newIdx];
};

// Handle transaction updates from sidebar
const handleTransactionUpdate = (updatedData) => {
    const idx = transactions.value.findIndex((t) => t.id === updatedData.id);
    if (idx !== -1) {
        transactions.value[idx] = {
            ...transactions.value[idx],
            ...updatedData,
        };
        // Update selected transaction if it's the same one
        if (selectedTransaction.value?.id === updatedData.id) {
            selectedTransaction.value = transactions.value[idx];
        }
    }
};

// Handle transaction deletion (soft delete)
const handleTransactionDelete = (data) => {
    const idx = transactions.value.findIndex((t) => t.id === data.id);
    if (idx !== -1) {
        // Mark as deleted (soft delete) - remove from visible list
        transactions.value.splice(idx, 1);
    }
};

// Handle transaction hide/show
const handleTransactionHide = (data) => {
    const idx = transactions.value.findIndex((t) => t.id === data.id);
    if (idx !== -1) {
        transactions.value[idx].is_hidden = data.is_hidden;
        // Update selected transaction if it's the same one
        if (selectedTransaction.value?.id === data.id) {
            selectedTransaction.value = { ...transactions.value[idx] };
        }
    }
};
</script>

<style scoped>
/* No max-width constraint */
</style>
